import { Link } from "react-router-dom";
import type { Work } from "@/types/work";
import { formatNumber } from "@/utils/format";

interface WorkCardProps {
  work: Work;
  layout?: "grid" | "row";
  rank?: number;
}

export default function WorkCard({ work, layout = "grid", rank }: WorkCardProps) {
  if (layout === "row") {
    return (
      <article className="group flex flex-col sm:flex-row gap-4 md:gap-5 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors">
        <Link
          to={`/work/${work.id}`}
          className="relative w-full sm:w-[210px] h-[150px] shrink-0 rounded-md overflow-hidden bg-background-200"
          aria-label={`阅读作品：${work.title}`}
        >
          <img src={work.cover} alt={work.title} className="w-full h-full object-cover object-top" />
          {typeof rank === "number" && (
            <span className="absolute top-0 left-0 w-8 h-8 flex items-center justify-center bg-primary-500 text-background-50 font-heading text-sm font-bold">
              {rank}
            </span>
          )}
        </Link>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-xs text-primary-700 bg-primary-100 px-2.5 py-1 rounded-full">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-price-tag-3-line" />
              </span>
              {work.categoryName}
            </span>
            {work.hot && (
              <span className="inline-flex items-center gap-1 text-xs text-accent-900 bg-accent-100 px-2.5 py-1 rounded-full">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-fire-line" />
                </span>
                热门参赛
              </span>
            )}
          </div>
          <h3 className="mt-2.5 font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
            <Link to={`/work/${work.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
              {work.title}
            </Link>
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{work.summary}</p>
          <div className="mt-auto pt-3 flex items-center gap-4 flex-wrap text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-user-3-line" />
              </span>
              {work.author}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-thumb-up-line" />
              </span>
              {formatNumber(work.likes)}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line" />
              </span>
              {formatNumber(work.views)}
            </span>
            <span className="ml-auto hidden sm:flex items-center gap-1.5">{work.date}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors">
      <Link
        to={`/work/${work.id}`}
        className="relative block w-full h-[190px] md:h-[210px] bg-background-200 overflow-hidden"
        aria-label={`阅读作品：${work.title}`}
      >
        <img
          src={work.cover}
          alt={work.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-xs text-background-50 bg-foreground-950/70 backdrop-blur px-2.5 py-1 rounded-full">
          {work.categoryName}
        </span>
        {work.hot && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs text-primary-950 bg-accent-400 px-2.5 py-1 rounded-full">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-fire-line" />
            </span>
            热门
          </span>
        )}
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
          <Link to={`/work/${work.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {work.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{work.summary}</p>
        <div className="mt-auto pt-4 flex items-center justify-between gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800 shrink-0">
              <i className="ri-user-3-line text-sm" />
            </span>
            <span className="truncate">{work.author}</span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-thumb-up-line" />
              </span>
              {formatNumber(work.likes)}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line" />
              </span>
              {formatNumber(work.views)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}